import { ChevronLeft } from '@carbon/icons-react';
import * as React from 'react';
import { Button, Loading, Tooltip } from 'vet-bones/design-system/components';

import * as copy from 'src/templates/copy';

import styles from 'src/components/resourceDialog/styles/dialogFooter.module.scss';

type FooterBackButtonProps = {
  onClick: () => void;
  label?: string;
  disabled?: boolean;
  showIcon?: boolean;
  testId?: string;
};

const FooterBackButton: React.VFC<FooterBackButtonProps> = ({
  onClick,
  label,
  disabled,
  showIcon,
  testId,
}) => (
  <Button
    disabled={disabled}
    leftIcon={showIcon ? ChevronLeft : undefined}
    onClick={onClick}
    size="md"
    testId={testId}
    variant="secondary"
  >
    {label || copy.components.projectResourceDialog.backButton}
  </Button>
);

type FooterCancelButtonProps = {
  onClick: () => void;
  disabled?: boolean;
};

const FooterCancelButton: React.VFC<FooterCancelButtonProps> = ({
  onClick,
  disabled,
}) => (
  <Button
    disabled={disabled}
    onClick={onClick}
    size="md"
    variant="secondary2-gray"
  >
    {copy.components.projectResourceDialog.cancelButton}
  </Button>
);

type FooterActionButtonProps = {
  onClick: () => void;
  label: string;
  disabled?: boolean;
  isLoading?: boolean;
  testId?: string;
  tooltipError?: string;
};

const FooterActionButton: React.VFC<FooterActionButtonProps> = ({
  onClick,
  label,
  disabled,
  isLoading,
  testId,
  tooltipError,
}) => {
  const button = (
    <Button
      disabled={disabled || Boolean(tooltipError)}
      onClick={onClick}
      rightIcon={isLoading ? () => <Loading size="sm" /> : undefined}
      size="md"
      testId={testId}
      variant="primary-accent"
    >
      {label}
    </Button>
  );

  if (tooltipError) {
    return (
      <Tooltip description={tooltipError} disabled={!tooltipError}>
        <div>{button}</div>
      </Tooltip>
    );
  }

  return button;
};

/**
 * Base footer options shared by all dialogs
 */
export type BaseFooterOptions = {
  onBack?: () => void;
  onNext?: () => void;
  onCancel?: () => void;
  nextLabel?: string;
  backLabel?: string;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
};

/**
 * Footer options for dialogs with Save functionality (preview dialogs)
 */
export type SaveFooterOptions = BaseFooterOptions & {
  onSave?: () => void;
  isSaveLoading?: boolean;
  isSaveDisabled?: boolean;
};

/**
 * Footer options for dialogs with Create functionality (resource creation)
 */
export type CreateFooterOptions = BaseFooterOptions & {
  onCreate?: () => void;
  onCreateAndMap?: () => void;
  isCreateLoading?: boolean;
  isCreateAndMapLoading?: boolean;
  createButtonError?: string;
};

export type DialogFooterProps = {
  footerOptions: SaveFooterOptions;
  nextButtonLabel?: string;
  saveButtonLabel?: string;
};

/**
 * Generic dialog footer for preview dialogs with Back, Cancel, Next, and Save buttons.
 * Used by VirtualGraphPreviewDialog and FileUploadPreviewDialog.
 */
export const DialogFooter: React.VFC<DialogFooterProps> = ({
  footerOptions,
  nextButtonLabel = copy.components.virtualGraphPreviewDialog.nextButton,
  saveButtonLabel = copy.components.projectResourceDialog.saveButton,
}) => {
  return (
    <div className={styles.dialogFooter}>
      <div className={styles.left}>
        {footerOptions.onBack ? (
          <FooterBackButton
            label={footerOptions.backLabel}
            onClick={footerOptions.onBack}
          />
        ) : null}
        {footerOptions.onCancel ? (
          <FooterCancelButton onClick={footerOptions.onCancel} />
        ) : null}
      </div>
      <div className={styles.right}>
        {footerOptions.onNext ? (
          <FooterActionButton
            disabled={
              footerOptions.isNextDisabled || footerOptions.isSaveLoading
            }
            isLoading={footerOptions.isNextLoading}
            label={footerOptions.nextLabel || nextButtonLabel}
            onClick={footerOptions.onNext}
          />
        ) : null}
        {footerOptions.onSave ? (
          <FooterActionButton
            disabled={
              footerOptions.isSaveDisabled || footerOptions.isNextLoading
            }
            isLoading={footerOptions.isSaveLoading}
            label={saveButtonLabel}
            onClick={footerOptions.onSave}
          />
        ) : null}
      </div>
    </div>
  );
};

export type ResourceDialogFooterProps = {
  footerOptions: CreateFooterOptions;
  showBackIcon?: boolean;
  backButtonTestId?: string;
  createButtonTestId?: string;
  createAndMapButtonTestId?: string;
  nextButtonTestId?: string;
};

/**
 * Dialog footer for project resource dialogs with navigation and action buttons.
 * Supports Back, Cancel, Next, Create, and Create & Map buttons.
 * Used by ProjectNewResourceDialog and will be used for edit flows.
 */
export const ResourceDialogFooter: React.VFC<ResourceDialogFooterProps> = ({
  footerOptions,
  showBackIcon = true,
  backButtonTestId,
  createButtonTestId,
  createAndMapButtonTestId,
  nextButtonTestId,
}) => {
  if (
    !footerOptions.onBack &&
    !footerOptions.onNext &&
    !footerOptions.onCancel &&
    !footerOptions.onCreate &&
    !footerOptions.onCreateAndMap
  ) {
    return null;
  }

  const isAnyCreateLoading =
    footerOptions.isCreateLoading || footerOptions.isCreateAndMapLoading;

  return (
    <div className={styles.dialogFooter}>
      <div className={styles.left}>
        {footerOptions.onBack ? (
          <FooterBackButton
            disabled={isAnyCreateLoading}
            label={footerOptions.backLabel}
            onClick={footerOptions.onBack}
            showIcon={showBackIcon}
            testId={backButtonTestId}
          />
        ) : null}
        {footerOptions.onCancel ? (
          <FooterCancelButton
            disabled={isAnyCreateLoading}
            onClick={footerOptions.onCancel}
          />
        ) : null}
      </div>
      <div className={styles.right}>
        {footerOptions.onCreate ? (
          <FooterActionButton
            disabled={footerOptions.isCreateAndMapLoading}
            isLoading={footerOptions.isCreateLoading}
            label={copy.components.projectResourceDialog.createButton}
            onClick={footerOptions.onCreate}
            testId={createButtonTestId}
            tooltipError={footerOptions.createButtonError}
          />
        ) : null}
        {footerOptions.onCreateAndMap ? (
          <FooterActionButton
            disabled={footerOptions.isCreateLoading}
            isLoading={footerOptions.isCreateAndMapLoading}
            label={copy.components.projectResourceDialog.createAndMapButton}
            onClick={footerOptions.onCreateAndMap}
            testId={createAndMapButtonTestId}
            tooltipError={footerOptions.createButtonError}
          />
        ) : null}
        {footerOptions.onNext ? (
          <FooterActionButton
            disabled={footerOptions.isNextDisabled || isAnyCreateLoading}
            isLoading={footerOptions.isNextLoading}
            label={
              footerOptions.nextLabel ||
              copy.components.projectResourceDialog.createButton
            }
            onClick={footerOptions.onNext}
            testId={nextButtonTestId}
          />
        ) : null}
      </div>
    </div>
  );
};
